import {Component, Prop, h, State, Element, Event, EventEmitter, Host} from '@stencil/core';
import {iconLevel} from '@/utils';
import {SnackbarLevels} from '@/types';

@Component({
    tag: 'joy-snackbar',
    styleUrl: 'style/snackbar.scss',
    shadow: true,
})
export class Snackbar {
    @Element() el!: HTMLJoySnackbarElement;
    /**
     * The criticality level of your notification. Range from simple info to error.
     */
    @Prop({reflect: true}) level: SnackbarLevels = 'success';
    /**
     * Not mandatory. Inject a title before the actual content
     */
    @Prop() header?: string;
    /**
     * HTML with your custom message injected in the component. It won't be escaped so please be careful with XSS !
     */
    @Prop() dangerousHtmlMessage!: string;
    /**
     * If you need to create an action link within the snackbar simply pass its text. Clicking on it will trigger a custom event
     */
    @Prop({reflect: true}) actionText?: string;
    /**
     * Time in ms the snackbar is displayed. Give 'forever' to be able to display the snackbar without timeout
     */
    @Prop({reflect: true}) duration: number | 'forever' = 5000;
    /**
     * Display an icon to close the notification manually
     */
    @Prop({reflect: true}) closable?: boolean = false;
    /**
     * By default, the snackbar will be displayed in a fixed position at the bottom of the page. You can override this behavior by setting position="relative"
     */
    @Prop({reflect: true}) position?: 'fixed' | 'relative' = 'fixed';

    @State() id!: string;

    /**
     * Fire snackbar custom action. Nothing is returned
     */
    @Event({
        eventName: 'joy-snackbar-trigger-action',
    })
    joySnackbarTriggerAction!: EventEmitter<void>;

    private triggerAction = () => {
        this.joySnackbarTriggerAction.emit();
    };

    private destroySnackbar = () => {
        this.position !== 'relative' && this.el.classList.add('joy-snackbar--hiding');

        setTimeout(() => {
            this.el.remove();
        }, 500);
    };

    get levelClass(): {[p: string]: boolean} {
        return {[`joy-snackbar_${this.level}`]: true};
    }

    get closeSnackbarCta(): HTMLJoyIconElement {
        return <joy-icon class="joy-snackbar__close-icon" onClick={this.destroySnackbar} name="cross" color="grey" data-close-notification />;
    }

    get icon(): HTMLJoyIconElement {
        return <joy-icon name={iconLevel(this.level)} />;
    }

    componentDidRender() {
        if (this.duration !== 'forever') {
            setTimeout(() => {
                return this.destroySnackbar();
            }, this.duration);
        }
    }

    render() {
        return (
            <Host
                data-level={this.level}
                class={{
                    'joy-snackbar__position___relative': this.position === 'relative',
                }}
            >
                <div
                    class={{
                        'joy-snackbar': true,
                        ...this.levelClass,
                    }}
                    id={this.id}
                    data-level={this.level}
                >
                    <div class="joy-snackbar__img">{this.icon}</div>
                    <div class="joy-snackbar__content">
                        {this.header && <strong>{this.header}</strong>}
                        <span innerHTML={this.dangerousHtmlMessage} />
                        {this.actionText && (
                            <p class="joy-snackbar__trigger___action" onClick={this.triggerAction}>
                                {this.actionText}
                            </p>
                        )}
                    </div>
                    {this.closable && this.closeSnackbarCta}
                </div>
            </Host>
        );
    }
}
